import { AnyAction, MiddlewareAPI } from 'redux';
import Services from 'store/Services';
import { ThunkDispatch } from 'store/store';

import { WidgetType } from 'widgets/WidgetType';
import { portfolioSummaryClearAggregatedDataAction } from './actions';
import State from 'store/State';
import { selectCombineSellsAndShorts } from 'app/store/appSettings/selectors';
import {
    PortfolioSummaryActionType,
    PortfolioSummaryInitialiseAction,
} from 'widgets/portfolioSummary/store/instances/actions';
import { buildLogger } from '@instinet-analytics/core-lib';
import { selectPortfolioSummaryAggregatedData } from './selectors';
import {
    selectAggregationField,
    selectAllWidgetsAggregationFields,
} from '../instances/configuration/selectors';
import { isSetConfigActionFor } from 'widgets/store/actions';
import { PortfolioSummaryConfigAction } from '../instances/configuration/actions';
import { PortfolioItemColumn } from 'portfolio/PortfolioItem';

export const portfolioSummaryMiddleware = (services: Services) => [
    recalculateAllOnCombineSellsAndShortsChangedMiddleware(services),
    initialiseSingleWidgetMiddleware(services),
    recalculateWidgetOnAggregationFieldChanged(services),
];

export function recalculateWidgetOnAggregationFieldChanged(services: Services) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const {
                loggingService,
                ordersStreamingService,
                portfolioService,
                portfolioSummaryDataService,
            } = services;

            const logger = buildLogger(
                'PortfolioSummary recalculateWidgetOnAggregationFieldChanged',
                loggingService
            );

            const prevState = middlewareAPI.getState();

            next(action);

            if (!isSetConfigActionFor(WidgetType.PORTFOLIO_SUMMARY, action)) {
                return;
            }

            const nextState = middlewareAPI.getState();
            const setConfigAction = action as PortfolioSummaryConfigAction;
            const widgetKey = setConfigAction.payload!.widgetKey;

            const prevAggregationField = selectAggregationField(
                prevState,
                widgetKey
            );
            const nextAggregationField = selectAggregationField(
                nextState,
                widgetKey
            );

            if (prevAggregationField === nextAggregationField) {
                return;
            }

            const prevAggregationFields =
                selectAllWidgetsAggregationFields(prevState);
            const nextAggregationFields =
                selectAllWidgetsAggregationFields(nextState);
            const alreadyAggregatingByNewField =
                prevAggregationFields.includes(nextAggregationField);
            const nothingAggregatingByPrevField =
                !nextAggregationFields.includes(prevAggregationField);

            // If the previous field is no longer used anywhere, clear the data to avoid unnecessary calculations as we receive updates...
            if (nothingAggregatingByPrevField) {
                middlewareAPI.dispatch(
                    portfolioSummaryClearAggregatedDataAction(
                        prevAggregationField
                    )
                );
            }

            // No need to calculate if another widget is already aggregating by this field...
            if (alreadyAggregatingByNewField) {
                logger.info(
                    'Not calculating as have already aggregated new aggregation field ' +
                        nextAggregationField
                );
                return;
            }

            // If this is a new column not yet in the portfolio, start/update the stream.
            // (when we get the new column, the filtered portfolio reset will be handled by recalculateAllWidgetsMiddleware)
            if (
                !portfolioService.isColumnInPortfolio(
                    nextAggregationField as PortfolioItemColumn
                )
            ) {
                logger.info(
                    'Starting stream for new aggregation field ' +
                        nextAggregationField
                );
                ordersStreamingService.startOrUpdateOrdersStream();
                return;
            }

            // Do it...
            logger.info(
                'Calculating for new aggregation field ' + nextAggregationField
            );
            portfolioSummaryDataService.calculateAggregatedData([
                nextAggregationField,
            ]);
        };
}

export function recalculateAllOnCombineSellsAndShortsChangedMiddleware(
    services: Services
) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService, portfolioSummaryDataService } = services;

            const logger = buildLogger(
                'PortfolioSummary recalculateAllWidgetsMiddleware',
                loggingService
            );

            const prevState = middlewareAPI.getState();

            next(action);

            const nextState = middlewareAPI.getState();

            const prevCombineSellsAndShorts =
                selectCombineSellsAndShorts(prevState);
            const nextCombineSellsAndShorts =
                selectCombineSellsAndShorts(nextState);

            if (prevCombineSellsAndShorts === nextCombineSellsAndShorts) {
                return;
            }

            logger.info('Recalculating all aggregation fields', action.type);

            portfolioSummaryDataService.recalculateAll();
        };
}

export function initialiseSingleWidgetMiddleware(services: Services) {
    return (middlewareAPI: MiddlewareAPI<ThunkDispatch<AnyAction>, State>) =>
        (next: ThunkDispatch<AnyAction>) =>
        (action: AnyAction) => {
            const { loggingService, portfolioSummaryDataService } = services;
            const logger = buildLogger(
                'PortfolioSummary initialiseSingleWidgetMiddleware',
                loggingService
            );

            next(action);

            if (
                action.type !==
                PortfolioSummaryActionType.PORTFOLIO_SUMMARY_INITIALISE
            ) {
                return;
            }

            const portfolioSummaryWidgetInitialisedAction =
                action as PortfolioSummaryInitialiseAction;

            const state = middlewareAPI.getState();

            const aggregationField = selectAggregationField(
                state,
                portfolioSummaryWidgetInitialisedAction.payload!.widgetKey
            );

            const aggregatedData = selectPortfolioSummaryAggregatedData(
                state,
                aggregationField
            );

            if (aggregatedData) {
                logger.info(
                    'Already have aggregated data for ' + aggregationField,
                    action
                );
                return;
            }

            logger.info(
                'Calculating aggregated data for ' + aggregationField,
                action
            );

            portfolioSummaryDataService.calculateAggregatedData([
                aggregationField,
            ]);
        };
}
